import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, of, startWith, map } from 'rxjs';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApiService } from '../../../../core/services/api.service';
import {
  Sprint, Task,
  TaskPriority, TaskStatus, TaskType,
  ProjectMemberBrief,
} from '../../../../models/task.model';

export interface TaskFormData {
  projectId: number;
  task?: Task;
  sprints?: Sprint[];
  defaultSprintId?: number | null;
  defaultType?: TaskType;
  parentId?: number;  // set when creating subtask (parent = story) or story (parent = epic)
}

@Component({
  selector: 'app-task-form-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    TitleCasePipe,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './task-form-dialog.component.html',
  styleUrl: './task-form-dialog.component.scss',
})
export class TaskFormDialogComponent implements OnInit {
  readonly data = inject<TaskFormData>(MAT_DIALOG_DATA);
  private readonly fb  = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly ref = inject(MatDialogRef<TaskFormDialogComponent>);

  isEdit = !!this.data.task;
  saving = false;
  saveError: string | null = null;

  readonly taskTypes:   TaskType[]     = ['epic', 'task', 'bug'];
  readonly typeIconMap: Record<string, string> = {
    epic: 'bolt', story: 'bookmark', task: 'check_box_outline_blank', bug: 'bug_report', subtask: 'subdirectory_arrow_right',
  };
  readonly statuses:    TaskStatus[]   = ['to_do', 'in_progress', 'in_review', 'done'];
  readonly priorities:  TaskPriority[] = ['critical', 'high', 'medium', 'low'];

  sprints: Sprint[] = [];
  members: ProjectMemberBrief[] = [];
  epicTasks: Task[] = [];  // task-type epics for the parent epic picker

  filteredMembers$: Observable<ProjectMemberBrief[]> = of([]);
  assigneeSearchCtrl = new FormControl('');
  selectedMember: ProjectMemberBrief | null = null;

  labelInput = new FormControl('');
  labels: string[] = [];

  get currentType(): TaskType { return this.form.controls.task_type.value as TaskType; }
  get isEpic():    boolean { return this.currentType === 'epic'; }
  get isSubtask(): boolean { return this.currentType === 'subtask'; }
  get isStoryLevel(): boolean { return ['story', 'task', 'bug'].includes(this.currentType); }
  get showSprint(): boolean { return this.isStoryLevel && !this.data.parentId; }
  get showParentEpic(): boolean { return this.isStoryLevel && !this.isEdit; }

  form = this.fb.group({
    title:               ['', [Validators.required, Validators.minLength(1)]],
    task_type:           [this.resolveDefaultType() as TaskType],
    description:         [''],
    acceptance_criteria: [''],
    status:              ['to_do' as TaskStatus],
    priority:            ['medium' as TaskPriority],
    assignee_id:         [null as number | null],
    sprint_id:           [this.data.defaultSprintId ?? null as number | null],
    parent_epic_id:      [null as number | null],
    story_points:        [null as number | null],
    due_date:            [''],
  });

  private resolveDefaultType(): TaskType {
    if (this.data.defaultType) return this.data.defaultType;
    if (this.data.parentId) {
      // If we have a parentId, figure out what type we're creating
      // (caller sets defaultType explicitly, so this is a fallback)
      return 'subtask';
    }
    return 'story';
  }

  ngOnInit(): void {
    // Load members
    this.api.get<{ members: ProjectMemberBrief[] }>(`/api/projects/${this.data.projectId}/members`).subscribe({
      next: ({ members }) => {
        this.members = members;
        this.setupMemberFilter();
        if (this.isEdit && this.data.task?.assignee) {
          const match = members.find(m => m.user_id === this.data.task!.assignee?.id);
          if (match) this.selectedMember = match;
        }
      },
    });

    // Load task-type epics for parent epic picker
    this.api.get<Task[]>(`/api/projects/${this.data.projectId}/tasks?task_type=epic`).subscribe({
      next: (epics) => { this.epicTasks = epics; },
    });

    // Load sprints
    if (this.data.sprints) {
      this.sprints = this.data.sprints.filter(s => s.status !== 'completed');
    } else {
      this.api.get<Sprint[]>(`/api/projects/${this.data.projectId}/sprints`).subscribe({
        next: (sprints) => { this.sprints = sprints.filter(s => s.status !== 'completed'); },
      });
    }

    if (this.isEdit) {
      const t = this.data.task!;
      this.labels = [...(t.labels ?? [])];
      this.form.patchValue({
        title:               t.title,
        task_type:           t.task_type,
        description:         t.description ?? '',
        acceptance_criteria: t.acceptance_criteria ?? '',
        status:              t.status,
        priority:     t.priority,
        assignee_id:  t.assignee?.id ?? null,
        sprint_id:    t.sprint_id,
        story_points: t.story_points,
        due_date:     t.due_date ?? '',
      });
    }
  }

  private setupMemberFilter(): void {
    this.filteredMembers$ = this.assigneeSearchCtrl.valueChanges.pipe(
      startWith(''),
      map(v => {
        const term = (typeof v === 'string' ? v : '').toLowerCase();
        return term
          ? this.members.filter(m => m.name.toLowerCase().includes(term) || m.email.toLowerCase().includes(term))
          : this.members;
      }),
    );
  }

  selectMember(m: ProjectMemberBrief): void {
    this.selectedMember = m;
    this.form.controls.assignee_id.setValue(m.user_id);
    this.assigneeSearchCtrl.setValue('');
  }

  clearAssignee(): void {
    this.selectedMember = null;
    this.assigneeSearchCtrl.setValue('');
    this.form.controls.assignee_id.setValue(null);
  }

  addLabel(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.value.trim();
    if (val && !this.labels.includes(val)) this.labels.push(val);
    input.value = '';
    this.labelInput.setValue('');
  }

  removeLabel(label: string): void { this.labels = this.labels.filter(l => l !== label); }

  typeLabel(t: string): string {
    return { epic: 'Epic', story: 'Story', task: 'Task', bug: 'Bug', subtask: 'Subtask' }[t] ?? t;
  }

  statusLabel(s: string): string {
    return { to_do: 'To Do', in_progress: 'In Progress', in_review: 'In Testing', done: 'Done' }[s] ?? s;
  }

  initial(name: string): string { return name ? name.charAt(0).toUpperCase() : '?'; }

  save(): void {
    if (this.form.invalid || this.saving) return;
    this.saveError = null;

    // Client-side guard when we have subtask data available (edit from detail dialog)
    const status = this.form.controls.status.value;
    if (this.isEdit && status === 'done') {
      const subs = this.data.task?.subtasks ?? [];
      const incomplete = subs.filter(s => s.status !== 'done').length;
      if (incomplete > 0) {
        this.saveError = `Cannot mark as done: ${incomplete} subtask${incomplete !== 1 ? 's' : ''} still in progress`;
        return;
      }
    }

    this.saving = true;

    const v = this.form.value;
    const payload: Record<string, unknown> = {
      title:               v.title!.trim(),
      task_type:           v.task_type,
      description:         v.description?.trim() || null,
      acceptance_criteria: this.isSubtask ? null : (v.acceptance_criteria?.trim() || null),
      status:              v.status,
      priority:     v.priority,
      assignee_id:  v.assignee_id ?? null,
      story_points: v.story_points ?? null,
      due_date:     v.due_date || null,
      labels:       this.labels,
    };

    // parent_id: set from data.parentId (passed by caller) or parent_epic_id control
    if (!this.isEdit) {
      if (this.data.parentId) {
        payload['parent_id'] = this.data.parentId;
      } else if (this.isStoryLevel && v.parent_epic_id) {
        payload['parent_id'] = v.parent_epic_id;
      }
    }

    // sprint_id: only for story/task/bug
    if (!this.isEpic && !this.isSubtask) {
      payload['sprint_id'] = v.sprint_id ?? null;
    }

    const req$ = this.isEdit
      ? this.api.patch<Task>(`/api/projects/${this.data.projectId}/tasks/${this.data.task!.id}`, payload)
      : this.api.post<Task>(`/api/projects/${this.data.projectId}/tasks`, payload);

    req$.subscribe({
      next:  (result) => this.ref.close(result),
      error: (err) => {
        this.saving = false;
        this.saveError = err?.error?.error ?? 'Something went wrong. Please try again.';
      },
    });
  }
}
